/**
 * The explicit-ID bulk surface's pure half (P8-T3): the selection, the request it becomes, and the
 * reviewed preview it is bound to.
 *
 * Sources: `REQUIREMENTS.md` `BAL-07` ("Bulk categorization and cash marking paid operate **only on
 * explicitly selected IDs**, with exact-set preview/confirmation and ownership validation ... **No
 * all-pages, filter-wide selection/action or individual transfer-leg/card-paid bulk mutation.** A
 * changed selection requires a new preview."), `SAFE-02`, `SAFE-04`, `UX-01` and
 * `FINANCIAL-ACCEPTANCE-EXAMPLES.md` F-30.
 *
 * Three decisions live here, and nowhere else in `apps/web`:
 *
 *   - **a selection is a list of ids, and nothing else.** There is no filter, page, account or
 *     "select all" anywhere in this module's types or its request builder, so the surface cannot
 *     describe a wider set than the person checked. A filter that happens to match every row is
 *     still not a selection: it is not a thing this module can express;
 *   - **the preview is the only thing a commit may name.** The request builder is the *sole* way a
 *     body reaches `POST /api/v1/mutation-previews`, and the commit names the stored preview's own
 *     id, so what was reviewed and what is written are one operation;
 *   - **a moved world is a new preview, not a commit.** Two of the three ways the world can move
 *     between the review and the write are visible on the client — the selection the person checked
 *     and the business day the server is on — and both are checked *before* the commit is offered.
 *     The third (a row's own version) is the API's `409 STALE_PREVIEW`.
 */

import { parseBusinessDate } from '@saldo/domain';
import type {
  BulkAction,
  BulkImpactBody,
  BulkMutationPreviewRequest,
  BulkPreviewBody,
  BulkRowRefusalReason,
  FieldError,
} from '@saldo/contracts';

import type { Mapped } from './forms';
import { formatMoney } from './money';

/**
 * The exact selected ids, in the order the person selected them.
 *
 * A list rather than a `Set` because the surface renders "3 selected" and, when a preview is stale,
 * has to say *what* moved; the order is only ever the order of selection and is normalised away by
 * the API (`normalizeSelection`), so two requests naming the same set are one intent.
 */
export type BulkSelection = readonly string[];

export const EMPTY_BULK_SELECTION: BulkSelection = [];

/** The contract's own bound (`IMPLEMENTATION-CONTRACTS.md` §3: "explicit bulk ≤500 IDs"). */
export const BULK_SELECTION_LIMIT = 500;

export function isSelected(selection: BulkSelection, entryId: string): boolean {
  return selection.indexOf(entryId) !== -1;
}

/** Check or uncheck one row, leaving every other checked row alone — including rows other pages hold. */
export function toggleSelection(selection: BulkSelection, entryId: string): BulkSelection {
  return isSelected(selection, entryId)
    ? selection.filter((id) => id !== entryId)
    : [...selection, entryId];
}

export function clearSelection(): BulkSelection {
  return EMPTY_BULK_SELECTION;
}

/** The action's own one optional field; there is no other input a request could carry. */
export interface BulkOptions {
  /** `bulk.categorize` only: the single optional category, where `null` is the deliberate clear. */
  readonly categoryId?: string | null;
  /** `bulk.pay` only: the one date the money moved, applied to every selected row. */
  readonly paidDate?: string;
}

/**
 * The request body for one action on one selection.
 *
 * The body's members are exactly the action's own: the ids, and at most one action-specific value.
 * A filter, a scope, a page or a select-all flag has nowhere to go — the API's schema is closed
 * (`additionalProperties: false`), which is asserted in `tests/postgres/src/bulk.test.ts`, and
 * nothing in this module could produce one anyway.
 */
export function toBulkRequest(
  action: BulkAction,
  selection: BulkSelection,
  options: BulkOptions = {},
): Mapped<BulkMutationPreviewRequest> {
  if (selection.length === 0) {
    return { ok: false, error: 'Select at least one row first.' };
  }
  if (selection.length > BULK_SELECTION_LIMIT) {
    return { ok: false, error: `Select at most ${BULK_SELECTION_LIMIT} rows at a time.` };
  }
  const entryIds = [...selection];
  if (action === 'bulk.pay') {
    const paidDate = options.paidDate;
    if (paidDate === undefined || !isBusinessDate(paidDate)) {
      return { ok: false, error: 'Choose the date the money moved (YYYY-MM-DD).' };
    }
    return { ok: true, body: { action, entryIds, paidDate } };
  }
  if (action === 'bulk.categorize') {
    return { ok: true, body: { action, entryIds, categoryId: options.categoryId ?? null } };
  }
  return { ok: true, body: { action, entryIds } };
}

function isBusinessDate(value: string): boolean {
  try {
    parseBusinessDate(value);
    return true;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ the review --- */

/** The words each action's button and preview carry. */
export function bulkActionLabel(action: BulkAction): string {
  if (action === 'bulk.pay') return 'Mark paid';
  if (action === 'bulk.categorize') return 'Set category';
  return 'Cancel entries';
}

/** One refused row's rule as a sentence fragment; the codes are the application's own vocabulary. */
export function refusalWord(code: string): string {
  const words: Record<BulkRowRefusalReason, string> = {
    unavailable: 'not one of your rows',
    'transfer-leg': 'a transfer leg, which bulk never touches',
    opening: 'an opening adjustment',
    canceled: 'already canceled',
    'card-paid-derived': 'a card row, whose paid state comes from its due date',
    'already-paid': 'already paid',
  };
  return (words as Record<string, string>)[code] ?? code;
}

/**
 * The API's named refusals as one sentence, or `null` when it named none.
 *
 * A refusal is never a silent skip: `F-30`'s "another owner's ID fails without partial writes" is
 * only useful if the surface says *which* id failed, so the per-id `fieldErrors` the API sends are
 * rendered instead of a generic "the change was refused".
 */
export function describeBulkRefusals(fieldErrors: readonly FieldError[]): string | null {
  const named = fieldErrors.filter((field) => field.path.startsWith('/entryIds/'));
  if (named.length === 0) return null;
  const parts = named.map((field) => {
    const id = field.path.slice('/entryIds/'.length);
    return `${id} — ${refusalWord(field.code)}`;
  });
  return `Nothing was changed. Refused: ${parts.join('; ')}.`;
}

/** What one previewed row shows, in the order the preview listed it. */
export interface BulkPreviewRowView {
  readonly entryId: string;
  readonly description: string;
  readonly amountText: string;
  readonly dateText: string;
  readonly stateText: string;
}

/** The reviewed preview, reduced to the figures and the set the surface enumerates. */
export interface BulkPreviewView {
  readonly id: string;
  readonly action: BulkAction;
  readonly actionLabel: string;
  readonly businessToday: string;
  /** The exact normalised set the commit will write — the same ids, sorted once by the API. */
  readonly entryIds: readonly string[];
  readonly rows: readonly BulkPreviewRowView[];
  /** The impact in one sentence, from the API's own counts. */
  readonly impactText: string;
  /** Per-account recorded-balance movements, as text; empty for a categorisation. */
  readonly accountTexts: readonly { readonly accountId: string; readonly text: string }[];
  readonly confirmLabel: string;
}

/**
 * One category as the reviewing surface knows it: the identity an impact carries, and the name a
 * person reads.
 *
 * Narrower than `CategoryBody` on purpose — the sentence needs a name for an id and nothing else —
 * so the surface can pass the categories it already holds, in whatever shape it holds them.
 */
export interface KnownCategory {
  readonly id: string;
  readonly name: string;
}

/**
 * The name a category is known by here, or `null` when this surface does not know that id.
 *
 * `null` rather than the id, because the fallback is the caller's decision: the sentence keeps the
 * identifier for a category it cannot name, and that is a different fact from knowing its name.
 */
function knownCategoryName(categories: readonly KnownCategory[], categoryId: string): string | null {
  const known = categories.find((category) => category.id === categoryId);
  return known === undefined ? null : known.name;
}

export function bulkPreviewView(
  preview: BulkPreviewBody,
  categories: readonly KnownCategory[],
): BulkPreviewView {
  return {
    id: preview.id,
    action: preview.action,
    actionLabel: bulkActionLabel(preview.action),
    businessToday: preview.businessToday,
    entryIds: [...preview.entryIds],
    rows: preview.rows.map((row) => ({
      entryId: row.entryId,
      description: row.description,
      amountText: formatMoney(BigInt(row.signedMinor), row.currency),
      dateText: row.dueDate ?? 'no due date',
      stateText:
        row.accountKind === 'card' ? (row.paid === true ? 'Card · paid' : 'Card') : row.paid === true ? 'Paid' : 'Pending',
    })),
    impactText: bulkImpactText(preview.action, preview.impact, preview.rows.length, categories),
    accountTexts: preview.impact.accounts.map((account) => ({
      accountId: account.accountId,
      text: `${formatMoney(BigInt(account.deltaMinor), account.currency)} on ${account.accountId}`,
    })),
    confirmLabel: `Confirm: ${bulkActionLabel(preview.action).toLowerCase()}`,
  };
}

/**
 * The impact in one sentence — the API's counts, never a figure recomputed here.
 *
 * `categories` is what the reviewing surface already holds. The impact carries an identifier, and a
 * sentence reading "change to `eeeeeeee-…`" asks the person to check a choice they made against a
 * uuid instead of against the name they picked from; the name is used where it is known, the
 * deliberate clear keeps its own reading, and the identifier stays the fallback for a category this
 * surface knows nothing about — one created in another tab, say. No read is added for it: the
 * categories are the ones the surface has already fetched.
 */
export function bulkImpactText(
  action: BulkAction,
  impact: BulkImpactBody,
  rowCount: number,
  categories: readonly KnownCategory[],
): string {
  if (action === 'bulk.pay') {
    return `${impact.paidCount} of ${rowCount} selected rows marked paid on ${impact.paidDate ?? '—'}.`;
  }
  if (action === 'bulk.categorize') {
    const target =
      impact.categoryId === null
        ? 'no category (cleared)'
        : knownCategoryName(categories, impact.categoryId) ?? impact.categoryId;
    return `${impact.changedCount} of ${rowCount} selected rows change to ${target}; ${impact.categorisedCount} touched.`;
  }
  return `${impact.cancelledCount} of ${rowCount} selected rows canceled.`;
}

/* --------------------------------------------------------------- the binding --- */

function sameIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const a = [...left].sort();
  const b = [...right].sort();
  return a.every((id, index) => id === b[index]);
}

/** Whether the reviewed set is still exactly the set currently checked. */
export function selectionStillReviewed(
  preview: Pick<BulkPreviewView, 'entryIds'>,
  selection: BulkSelection,
): boolean {
  return sameIds(preview.entryIds, selection);
}

/** Whether the reviewed business day is still the day the server is on. */
export function todayStillReviewed(
  preview: Pick<BulkPreviewView, 'businessToday'>,
  businessToday: string,
): boolean {
  return preview.businessToday === businessToday;
}

/**
 * Why the commit is not available, or `null` when it is.
 *
 * Two of the three moves a `BAL-07` preview is invalidated by are visible here: a changed selection
 * and a changed business day. The third — a selected row's own version — can only be seen by the
 * API, which is why the commit's `409 STALE_PREVIEW` is worded as needing a new preview too.
 */
export function commitBlocker(
  preview: Pick<BulkPreviewView, 'entryIds' | 'businessToday'>,
  selection: BulkSelection,
  businessToday: string,
): string | null {
  if (!selectionStillReviewed(preview, selection)) {
    return 'The selection changed since this preview. Take a new preview.';
  }
  if (!todayStillReviewed(preview, businessToday)) {
    return 'The business day moved since this preview. Take a new preview.';
  }
  return null;
}
